import { API_URL } from '@/constants/config';
import { getAuthHeader } from '~/modules/common/utils/cookieUtils';

const base = API_URL + '/v3.5';

/**
 * User Service
 *
 * @param {object} http axios instance from nuxt context
 * @param {object} cookies cookies from nuxt context
 */
export const userService = (http, cookies) => ({
  /**
   * Block user
   *
   * @param {string} id user id to block
   */
  async blockUser(id) {
    const body = { target: id };
    const { message, status } = await http.$post(`${base}/social/me/blocked`, body, {
      headers: getAuthHeader(cookies),
    });
    return { message, status };
  },

  /**
   * Unblock user
   *
   * @param {string} id user id to unblock
   */
  async unBlockUser(id) {
    const { message, status } = await http.$delete(`${base}/social/me/blocked/${id}`, {
      headers: getAuthHeader(cookies),
    });
    return { message, status };
  },

  /**
   * Follow user
   *
   * @param {string} id user id to block
   */
  async followUser(id) {
    const body = { target: id };
    const { message, status } = await http.$post(`${base}/social/me/following`, body, {
      headers: getAuthHeader(cookies),
    });
    return { message, status };
  },

  /**
   * Unfollow user
   *
   * @param {string} id user id to unblock
   */
  async unFollowUser(id) {
    const { message, status } = await http.$delete(`${base}/social/me/following/${id}`, {
      headers: getAuthHeader(cookies),
    });
    return { message, status };
  },

  /**
   * Upload avatar
   *
   * @param {object} formData
   *
   * @example
   * const image = selectedFiles[0] // input'dan gelen
   * const formData = new FormData();
   * formData.append('image', image)
   * uploadAvatar(formData);
   */
  async uploadAvatar(formData) {
    const { data } = await http.$put(`${base}/users/me/avatar`, formData, {
      headers: {
        ...getAuthHeader(cookies),
        'Content-Type': 'multipart/form-data',
      },
      timeout: 15000,
    });
    return { data };
  },

  /**
   * Reset avatar
   */
  async resetAvatar() {
    const { data } = await http.$delete(`${base}/users/me/avatar`, {
      headers: {
        ...getAuthHeader(cookies),
      },
    });
    return { data };
  },

  /**
   * Upload cover
   *
   * @param {object} formData
   *
   * @example
   * const image = selectedFiles[0] // input'dan gelen
   * const formData = new FormData();
   * formData.append('image', image)
   * uploadCover(formData);
   */
  async uploadCover(formData) {
    const { data } = await http.$put(`${base}/users/me/cover`, formData, {
      headers: {
        ...getAuthHeader(cookies),
        'Content-Type': 'multipart/form-data',
      },
      timeout: 15000,
    });
    return { data };
  },

  /**
   * Reset cover
   */
  async resetCover() {
    const { data } = await http.$delete(`${base}/users/me/cover`, {
      headers: {
        ...getAuthHeader(cookies),
      },
    });
    return { data };
  },

  /**
   * Update user profile cover offset
   *
   * @param {Number} coverOffset
   */
  async updateCoverOffset(coverOffset) {
    const params = {
      coverOffset,
    };
    const { data } = await http.$put(`${base}/users/me/about`, params, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },

  /**
   * Update user information
   *
   * @param {string} email user email
   * @param {string} name user name
   * @param {string} gender user gender
   * @param {string} website user website
   * @param {string} country user country
   * @param {string} city user city
   * @param {string} birthdate user birthdate
   * @param {string} occupation user occupation
   * @param {string} premium user about data
   */
  async updateUserInformation({ email, username, name, gender, website, country, city, birthdate, occupation, premium }) {
    const params = {
      email,
      username,
      name,
      gender,
      website,
      country,
      city,
      birthdate,
      occupation,
      premium,
    };
    const { data } = await http.$put(`${base}/users/me`, params, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },

  /**
   * Update password
   *
   * @param {string} oldPassword - old password
   * @param {string} password - password
   */
  async updatePassword(oldPassword, password) {
    const params = {
      oldPassword,
      password,
    };
    const { data } = await http.$put(`${base}/users/me/password`, params, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },
});
